import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const INVITE_TOKEN_KEY = 'lr_pending_network_invite_token';
const BOARD_PATH = '/lr-direct/board';

/**
 * Phase D — Magic-link invitation flow (Option B).
 * After a broker signs up and subscribes to LR Direct Private/Full, this hook
 * picks up the pending invite token (stashed in sessionStorage by AuthPage) and
 * auto-accepts the invitation server-side. On success it routes to the LR Direct
 * board with a welcome toast.
 *
 * Uses window.location for navigation because this hook is mounted from
 * LRDirectGateProvider, which sits above the Router in App.tsx (useNavigate
 * would throw outside a Router context).
 */
export function useAutoAcceptNetworkInvite(userId?: string | null) {
  const inFlightRef = useRef(false);


  useEffect(() => {
    if (!userId) return;
    if (inFlightRef.current) return;

    let token: string | null = null;
    try {
      token = sessionStorage.getItem(INVITE_TOKEN_KEY);
    } catch {
      return;
    }
    if (!token) return;

    inFlightRef.current = true;

    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke('accept-network-invitation', {
          body: { token, action: 'accept' },
        });

        // Subscription not yet active — leave token in place so the next mount
        // (after webhook settles) can retry.
        if ((data as any)?.requires_subscription) {
          inFlightRef.current = false;
          return;
        }

        if (error) throw error;

        if ((data as any)?.already_member) {
          // Clicker is already an accepted member — reassure them instead of showing a failure.
          try { sessionStorage.removeItem(INVITE_TOKEN_KEY); } catch {}
          toast.success("You're already a member of this network — you have full access.");
          window.location.assign(BOARD_PATH);
        } else if ((data as any)?.success) {
          try { sessionStorage.removeItem(INVITE_TOKEN_KEY); } catch {}
          const shipperName = (data as any)?.shipper_name as string | undefined;
          toast.success(
            shipperName
              ? `Welcome to ${shipperName}'s private network!`
              : `You're in! Welcome to the private network.`,
            {
              action: {
                label: 'View Loads',
                onClick: () => { window.location.assign(BOARD_PATH); },
              },
            }
          );
          window.location.assign(BOARD_PATH);
        } else if ((data as any)?.email_mismatch) {
          // G4 — the invite went to a different address. Keep the security guard
          // (non-members can't consume someone else's invite), but give clear
          // instructions to request access via the existing flow.
          try { sessionStorage.removeItem(INVITE_TOKEN_KEY); } catch {}
          toast.error("This invite was sent to a different address", {
            description: "To join this network, go to My Network → click Find a Shipper → add the network key → request to join.",
            duration: 15000,
          });
        } else if ((data as any)?.error) {
          // Token expired / invalid — clear silently. Broker can find shipper manually.
          try { sessionStorage.removeItem(INVITE_TOKEN_KEY); } catch {}
          console.warn('[auto-accept-invite] token rejected:', (data as any).error);
        }


      } catch (err) {
        // Network error or unexpected exception — keep token so broker can retry.
        console.error('[auto-accept-invite] failed:', err);
        inFlightRef.current = false;
      }
    })();
  }, [userId]);
}
